import type { LoginProvidersInner, User } from "@eai-toolkit/client";
import {
  LoginApi,
  LoginApiAxiosParamCreator,
  MeApi,
} from "@eai-toolkit/client";
import { tracker } from "@openreplay/tracker";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { AxiosError } from "axios";

import { apiConfiguration, axiosInstance } from "@helpers/api";

import Config from "../../config";

export interface AuthState {
  providers: LoginProvidersInner[] | null;
  terms: string | null;
  user: User | null;
  isLoading: boolean;
  authError: string | null;
  redirect: string | null;
}

const initialState: AuthState = {
  providers: null,
  terms: null,
  user: null,
  isLoading: false,
  authError: null,
  redirect: null,
};

const meApi = new MeApi(apiConfiguration, "", axiosInstance);
const loginApi = new LoginApi(apiConfiguration, "", axiosInstance);
const loginApiParamsCreator = LoginApiAxiosParamCreator(apiConfiguration);

// Helper functions for OpenReplay
export enum Environment {
  Prod = "prod",
  Dev = "dev",
  LocalDev = "localdev",
}

const environmentConfig: Record<Environment, string> = {
  [Environment.Prod]: "UI1P6bXMuSQGHb4EMZIG",
  [Environment.Dev]: "L3J46alAeoTeDNdsu1vR",
  [Environment.LocalDev]: "4d1mglBiZwO8t2se4BtR",
};

export const hostnameToEnvironment: Record<string, Environment> = {
  "console-dev.elementai.com": Environment.Dev,
  "console.elementai.com": Environment.Prod,
  localhost: Environment.LocalDev,
  "toolkit-sp.yul201.service-now.com": Environment.Prod,
};

const getBrowserSizeCategory = (width: number, height: number): string => {
  if (width * height <= 1440 * 900) {
    return "Small (≤1440x900)";
  } else if (width * height <= 1680 * 1050) {
    return "Medium (>1440x900 to ≤1680x1050)";
  } else if (width * height <= 1920 * 1080) {
    return "Large (>1680x1050 to ≤1920x1080)";
  } else if (width * height <= 2560 * 1440) {
    return "XLarge (>1920x1080 to ≤2560x1440)";
  } else {
    return "XXLarge (>2560x1440)";
  }
};

export const getProjectKeyForUrl = (url: string): string => {
  const { hostname } = new URL(url);
  const environment = hostnameToEnvironment[hostname];
  return (
    environmentConfig[environment] ?? environmentConfig[Environment.LocalDev]
  );
};

const getTrackerConfigData = () => {
  const { hostname } = new URL(window.location.href);
  const environment = hostnameToEnvironment[hostname];
  return {
    projectKey: getProjectKeyForUrl(window.location.href),
    environment,
    hostname,
  };
};

// Async thunks
export const fetchLoggedInUser = createAsyncThunk(
  "auth/fetchLoggedInUser",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await meApi.v1MeGet({});
      const { projectKey, environment, hostname } = getTrackerConfigData();

      if (!environment) {
        console.error("Unknown environment.");
      }

      // Start OpenReplay tracker
      tracker.configure({
        projectKey,
        ingestPoint: "https://openreplay.console.elementai.com/ingest",
        __DISABLE_SECURE_MODE: environment === Environment.LocalDev,
      });
      tracker.start({
        userID: data.mail || data.name || data.id,
        metadata: {
          organization: data.organization || "",
          id: data.id || "",
          hostname,
          browserSizeCategory: getBrowserSizeCategory(
            window.innerWidth,
            window.innerHeight,
          ),
          browserSize: `${window.innerWidth}x${window.innerHeight}`,
        },
      });

      return data as User;
    } catch (err) {
      if (!Config.isDev) {
        window.location.href = `/login?current_url=${encodeURIComponent(window.location.href)}`;
      }
      return rejectWithValue((err as AxiosError).message);
    }
  },
);

export const fetchProviders = createAsyncThunk(
  "auth/fetchProviders",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await loginApi.getProviders();
      return data;
    } catch (err) {
      const axiosError = err as AxiosError;
      let errorMessage = "Unknown error";
      if (axiosError.response?.status) {
        if (
          axiosError.response.status > 399 &&
          axiosError.response.status < 500
        ) {
          errorMessage = "Client error, bad request";
        } else if (
          axiosError.response.status > 499 &&
          axiosError.response.status < 600
        ) {
          errorMessage = "Internal server error, try again later";
        }
      }
      return rejectWithValue(errorMessage);
    }
  },
);

export const fetchTerms = createAsyncThunk(
  "auth/fetchTerms",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await meApi.v1MeTermsGet();
      return data;
    } catch (err) {
      console.error(err);
      return rejectWithValue((err as Error).message);
    }
  },
);

export const agreeToTerms = createAsyncThunk(
  "auth/agreeToTerms",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await meApi.v1MeTermsPut();
      return data;
    } catch (err) {
      console.error(err);
      return rejectWithValue((err as Error).message);
    }
  },
);

export const oidcLogin = createAsyncThunk(
  "auth/oidcLogin",
  async (
    {
      providerId,
      loginChallenge,
    }: { providerId: string; loginChallenge: string },
    { rejectWithValue },
  ) => {
    try {
      const params = await loginApiParamsCreator.oidcLogin(
        providerId,
        loginChallenge,
      );
      window.location.href = params.url;
      return params.url;
    } catch (err) {
      return rejectWithValue((err as AxiosError).message);
    }
  },
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setRedirect: (state, action: PayloadAction<string | null>) => {
      state.redirect = action.payload;
    },
    clearAuthError: (state) => {
      state.authError = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch logged in user
    builder
      .addCase(fetchLoggedInUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchLoggedInUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
      })
      .addCase(fetchLoggedInUser.rejected, (state, action) => {
        state.isLoading = false;
        state.authError = action.payload as string;
      });

    // Fetch providers
    builder
      .addCase(fetchProviders.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchProviders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.providers = action.payload;
      })
      .addCase(fetchProviders.rejected, (state, action) => {
        state.isLoading = false;
        state.authError = action.payload as string;
      });

    // Fetch terms
    builder.addCase(fetchTerms.fulfilled, (state, action) => {
      state.terms = action.payload;
    });

    // Agree to terms
    builder.addCase(agreeToTerms.fulfilled, (state, action) => {
      if (state.user) {
        state.user = { ...state.user, termsAgreed: action.payload };
      }
    });
  },
});

export const { setRedirect, clearAuthError } = authSlice.actions;

export default authSlice.reducer;
